import React, { useMemo, useState } from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';

/**
 * Removal confirmation. The email has to be typed out, because the trigger is a
 * small icon on one of ~160 rows and a stray click is otherwise unrecoverable.
 *
 * It also spells out what removal does NOT cover — chiefly the Firebase Auth
 * login, which a browser cannot delete for another account.
 */
export default function DeleteEmployeeModal({ isOpen, onClose, user, users, onConfirm }) {
  const [typed, setTyped] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);

  const reports = useMemo(() => {
    if (!user) return [];
    return (users || []).filter(u => (u.managerEmail || '').toLowerCase() === (user.email || '').toLowerCase());
  }, [user, users]);

  if (!isOpen || !user) return null;

  const confirmed = typed.trim().toLowerCase() === (user.email || '').toLowerCase();

  const close = () => { setTyped(''); setError(''); setProgress(null); onClose(); };

  const run = async () => {
    setIsBusy(true);
    setError('');
    const result = await onConfirm(user, setProgress);
    setIsBusy(false);
    if (result?.ok) {
      close();
    } else {
      const first = result?.errors?.[0];
      setError(first ? `${first.stage}: ${first.message}` : 'Could not remove this user.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
              <Trash2 size={18} className="text-red-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Remove employee</h2>
              <p className="text-xs text-gray-500">This cannot be undone</p>
            </div>
          </div>
          <button type="button" onClick={close} disabled={isBusy} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700">
            Removing <span className="font-semibold">{user.name || user.email}</span> deletes their profile and
            every evaluation stored under it, across both financial years.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 space-y-2">
            <p className="flex items-start gap-1.5 text-[11px] font-semibold text-amber-900">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-600" />
              What this does not remove
            </p>
            <ul className="text-[11px] text-amber-800 space-y-1 pl-5 list-disc">
              <li>
                <span className="font-semibold">Their login.</span> A browser cannot delete another
                account&rsquo;s Firebase login, so it stays until the cleanup script is run. They will be
                signed out and see nothing, but their email address stays taken and cannot be re-added here.
              </li>
              <li>
                <span className="font-semibold">Their Individual KRs.</span> Any KR they own keeps pointing at
                them and will show as an unknown owner. Reassign from OKR Tracking if the work continues.
              </li>
              {reports.length > 0 && (
                <li>
                  <span className="font-semibold">{reports.length} {reports.length === 1 ? 'person' : 'people'}</span> {reports.length === 1 ? 'reports' : 'report'} to
                  them ({reports.slice(0, 3).map(r => r.name || r.email).join(', ')}
                  {reports.length > 3 ? `, +${reports.length - 3} more` : ''}) and will be left without a
                  working manager link.
                </li>
              )}
            </ul>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Type <span className="font-mono text-gray-900">{user.email}</span> to confirm
            </label>
            <input
              type="text" value={typed} onChange={(e) => setTyped(e.target.value)} disabled={isBusy}
              autoComplete="off"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500 outline-none"
              placeholder={user.email}
            />
          </div>

          {progress && (
            <p className="text-[11px] text-gray-500">
              {progress.stage === 'scan' && `Found ${progress.found} document${progress.found === 1 ? '' : 's'} to remove…`}
              {progress.stage === 'delete' && `Removed ${progress.done} of ${progress.total}…`}
              {progress.stage === 'done' && 'Finishing up…'}
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
              {error} — nothing else was touched, and running this again is safe.
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={close} disabled={isBusy}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button type="button" onClick={run} disabled={!confirmed || isBusy}
              className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isBusy ? 'Removing…' : 'Remove permanently'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
